import React, { useState } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Typography,
  Stack
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ClearIcon from '@mui/icons-material/Clear';
import { PrefillReportSummary } from '../../types/dailyReport';

export interface PrefillBannerProps {
  sourceReportId: number;
  sourceReportDate: string;
  availableReports: PrefillReportSummary[];
  onChangeSource: (reportId: number) => void;
  onClear: () => void;
}

const formatDate = (iso: string): string => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
};

export const PrefillBanner: React.FC<PrefillBannerProps> = ({
  sourceReportId,
  sourceReportDate,
  availableReports,
  onChangeSource,
  onClear
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleSelect = (reportId: number) => {
    handleClose();
    if (reportId !== sourceReportId) onChangeSource(reportId);
  };

  return (
    <Box
      sx={{
        bgcolor: '#e3f2fd',
        border: '1px solid #90caf9',
        borderRadius: 1,
        px: { xs: 1.5, sm: 2 },
        py: 1,
        mb: 2
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={1}
      >
        <Stack direction="row" alignItems="center" spacing={1} flexGrow={1} minWidth={0}>
          <InfoOutlinedIcon fontSize="small" sx={{ color: '#1976d2', flexShrink: 0 }} />
          <Typography variant="body2" sx={{ color: '#0d47a1', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {formatDate(sourceReportDate)} の日報から職長・メンバー・車両を引用しました
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<CalendarMonthIcon />}
            onClick={handleOpen}
            sx={{ minHeight: 36 }}
          >
            日付変更
          </Button>
          <Button
            size="small"
            variant="text"
            startIcon={<ClearIcon />}
            onClick={onClear}
            sx={{ minHeight: 36, color: '#0d47a1' }}
          >
            クリア
          </Button>
        </Stack>
      </Stack>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{ paper: { sx: { maxHeight: 360, minWidth: 240 } } }}
      >
        {availableReports.map(r => (
          <MenuItem
            key={r.id}
            selected={r.id === sourceReportId}
            onClick={() => handleSelect(r.id)}
          >
            <Stack direction="column" spacing={0}>
              <Typography variant="body2">{formatDate(r.date)}</Typography>
              <Typography variant="caption" color="text.secondary">
                {r.supervisor} ・ {r.manPower}人工
              </Typography>
            </Stack>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};
